import { PieceType } from '../../pieces/types';

import { ApplicationState } from '../../../types';

import { getPieceValue, executePiece } from '../index';

import { ExecutePieceReturnType, getNextPieceIdForComputedField } from './index';
import { DefaultFlowchartProcessState } from '../../types';
import { VariableValueType } from '../../../../helpers/common-types';
import { CustomField, FieldType, getValidCustomFieldValueForInput } from '../../../custom-fields/types';

export function getUserCustomFieldPieceValue(applicationState: ApplicationState, processState: DefaultFlowchartProcessState, userId: string, customField: CustomField, pieceId: string): VariableValueType {
    const piecesData = applicationState.flowchart.pieces;
    const piece = piecesData.byId[pieceId];
    const userData = applicationState.users;

    const functionShortHand = getUserCustomFieldPieceValue.bind({}, applicationState, processState, userId, customField);

    switch (piece.type) {
        case PieceType.CUSTOM_FIELD:
            if (!piece.customField) {
                throw new Error('A custom field needs to be selected');
            }

            if (piece.customFieldOption) {
                return userData.customFieldOptions.byId[piece.customFieldOption].name;
            }

            const customField = userData.customFields.byId[piece.customField];

            if (customField.isComputed) {
                let startPiece: string | undefined;

                if (customField.startPiece) {
                    startPiece = customField.startPiece.piece;
                }

                if (typeof startPiece === 'undefined') {
                    return undefined;
                }

                const computedValue = getUserCustomFieldPieceValue(applicationState, processState, userId, customField, startPiece);

                return computedValue;
            }

            let customFieldValue = processState.customFields[customField.id];

            if (customField.type === FieldType.SINGLE_SELECT) {
                if (!!customFieldValue) {
                    if (typeof customFieldValue !== 'string') {
                        throw new Error('A single select must have a custom field type');
                    }

                    customFieldValue = userData.customFieldOptions.byId[customFieldValue].name;

                }
            }

            if (customField.type === FieldType.MULTI_SELECT) {
                if (!!customFieldValue) {
                    if (!Array.isArray(customFieldValue)) {
                        throw new Error('A multi select must have an array');
                    }

                    customFieldValue = customFieldValue.map(customFieldOptionId => userData.customFieldOptions.byId[customFieldOptionId].name);

                }
            }

            return customFieldValue;
        default:
            return getPieceValue(applicationState, processState, pieceId, functionShortHand);
    }
}

// If it returns false, stop execution and display what needs to be displayed. Otherwise, feel free to get the next piece and continue executing
function executePieceForUserComputedField(applicationState: ApplicationState, processState: DefaultFlowchartProcessState, pieceId: string, userId: string, customField: CustomField): ExecutePieceReturnType {
    const piecesData = applicationState.flowchart.pieces;
    const piece = piecesData.byId[pieceId];

    const getPieceShorthandTemp = getUserCustomFieldPieceValue.bind({}, applicationState, processState, userId);
    const getPieceShorthand = getPieceShorthandTemp.bind({}, customField);

    switch (piece.type) {

        case PieceType.RETURN:

            if (typeof piece.returnValue === 'undefined') {
                throw new Error('The return piece must have a variable')
            }

            const returnVariablePiece = piecesData.byId[piece.returnValue];
            const returnVariableValue = getPieceShorthand(returnVariablePiece.id);

            return {
                canContinueExecution: false,
                returnValue: returnVariableValue,
            };

        default:
            const canContinueExecution = executePiece(applicationState, processState, pieceId, getPieceShorthand);

            return {
                canContinueExecution,
                returnValue: undefined,
            };
    }
}

export function getUserComputedFieldValue(applicationState: ApplicationState, processState: DefaultFlowchartProcessState, startPiece: string | undefined, userId: string, customField: CustomField) {
    let executionResult: ExecutePieceReturnType;
    const getPieceShorthandTemp = getUserCustomFieldPieceValue.bind({}, applicationState, processState, userId);
    const getPieceShorthand = getPieceShorthandTemp.bind({}, customField);

    do {
        const nextPieceId = getNextPieceIdForComputedField(applicationState, processState, startPiece, getPieceShorthand);

        if (typeof nextPieceId === 'undefined') {
            throw new Error('The next piece does not exist');
        }

        executionResult = executePieceForUserComputedField(applicationState, processState, nextPieceId, userId, customField);
    } while (executionResult.canContinueExecution);

    const returnValue = executionResult.returnValue;
    return getValidCustomFieldValueForInput(customField, returnValue, applicationState.users.customFieldOptions);
}